import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  EMPTY_PROCUREMENT_LABEL,
  fetchProcurementCatalog,
  invalidateProcurementCatalogCache,
  resolveGrnNumberFromList,
  resolvePoNumberFromList,
} from '../utils/procurementDisplay';

/**
 * Loads PO + GRN catalogs once (module-cached) and exposes id → number resolution.
 */
export default function usePoGrnLookup() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchProcurementCatalog()
      .then((c) => { if (alive) setCatalog(c); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const refresh = useCallback(() => {
    invalidateProcurementCatalogCache();
    setLoading(true);
    return fetchProcurementCatalog({ force: true }).then((c) => {
      setCatalog(c);
      setLoading(false);
      return c;
    });
  }, []);

  const resolvePoNumber = useCallback((poId, inlineNumber) => {
    if (inlineNumber) return inlineNumber;
    if (!poId) return EMPTY_PROCUREMENT_LABEL;
    return catalog
      ? catalog.pos.resolveById(poId)
      : resolvePoNumberFromList(poId, []);
  }, [catalog]);

  const resolveGrnNumber = useCallback((grnId, inlineNumber) => {
    if (inlineNumber) return inlineNumber;
    if (!grnId) return EMPTY_PROCUREMENT_LABEL;
    return catalog
      ? catalog.grns.resolveById(grnId)
      : resolveGrnNumberFromList(grnId, []);
  }, [catalog]);

  return useMemo(() => ({
    pos: catalog?.pos?.items ?? [],
    grns: catalog?.grns?.items ?? [],
    loading,
    resolvePoNumber,
    resolveGrnNumber,
    refresh,
  }), [catalog, loading, resolvePoNumber, resolveGrnNumber, refresh]);
}
